import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { getApi } from '../../../api/api'
import { EmailStage, AfterActions, CommonModals, Group } from '../../../common/types/enums'
import { USER_ROUTE, emailLoginFormValidations, minContacts } from '../../../common/utils/constants'
import { delayAction } from '../../../common/utils/functions/delayAction'
import { setCommonModalState } from '../../../store/slices/commonModalSlice'
import { setTopicList } from '../../../store/slices/chatSlice'
import { openPayment } from '../../../store/slices/paymentSlice'
import { jwtDecode } from 'jwt-decode'
import {
  AccessPayload,
  EmailLoginFormData,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../common/types/interfaces'
import { useChatForOrders } from '../../../common/hooks/useChatForOrders'
import { useValidation } from '../../../common/hooks/useValidation'
import { useInput } from '../../../common/hooks/useInput'
import useInterval from '@use-it/interval'

const initialValue: EmailLoginFormData = {
  email: '',
  code: '',
}

const validations: ValidationRulesInterface[] = emailLoginFormValidations

export const useEmailLoginForm = (setModalActive: (state: boolean) => void) => {
  const [data, setData] = useState<EmailLoginFormData>(initialValue)
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const [disabled, setDisabled] = useState<boolean>(true)
  const [checkDisabled, setCheckDisabled] = useState<boolean>(true)
  const [stage, setStage] = useState<EmailStage>(EmailStage.EMAIL_INPUT)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isLogin, setIsLogin] = useState(true)
  const [legalityChecked, setLegalityChecked] = useState(false)
  const [timer, setTimer] = useState(0)
  const [registerMessage, setRegisterMessage] = useState('')

  const [api] = useState(() => getApi())
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { openChatWindow } = useChatForOrders()

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )

  const { inputHandler } = useInput(setData)

  useInterval(() => {
    if (timer > 0) {
      setTimer((prev) => prev - 1)
    }
  }, 1000)

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [data])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    if (stage === EmailStage.CODE_INPUT && data.code.length === 6 && isLogin) {
      verifyCode()
    }
  }, [data.code, stage, isLogin])

  useEffect(() => {
    setDisabled(
      validationResult.find((el) => el.name === 'email')?.notValid ||
        !validationResult.find((el) => el.name === 'email'),
    )
    setCheckDisabled(
      validationResult.find((el) => el.name === 'code')?.notValid ||
        !validationResult.find((el) => el.name === 'code'),
    )
  }, [validationResult])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const getFieldByGroup = (group: Group) => {
    switch (group) {
      case Group.CARGO:
        return 'cargoId'
      case Group.TRANSPORT:
        return 'transportId'
      default:
        return 'slotId'
    }
  }

  const getTopicList = useCallback(
    async (userId: number) => {
      try {
        const result = await api.getTopicList(userId)
        if (result.length > 0) dispatch(setTopicList(result))
        return result
      } catch (error) {}
    },
    [api, dispatch],
  )

  const requestCode = useCallback(async () => {
    if (disabled || loading) return

    try {
      setLoading(true)
      setMessage('Отправка кода...')

      await api.requestEmailCode({ email: data.email })

      setStage(EmailStage.CODE_INPUT)
      setTimer(60)
      setMessage('')
      setRegisterMessage('')
    } catch (error: any) {
      const errorText = (error?.response?.data?.message || error?.message || '').toLowerCase()

      if (errorText.includes('timeout') && errorText.includes('exceeded')) {
        setMessage('Время ожидания истекло, попробуйте позже')
      } else if (error.response?.status === 401) {
        setMessage('Ошибка авторизации, попробуйте позже')
      } else if (error.response?.status === 409) {
        setMessage('Email уже используется')
      } else if (error.response?.status === 429) {
        setMessage('Слишком много попыток, повторите через минуту')
      } else {
        setMessage(error.message || 'Ошибка отправки кода')
      }
      setStage(EmailStage.ERROR)
    } finally {
      setLoading(false)
    }
  }, [data.email, api, disabled, loading])

  const verifyCode = useCallback(async () => {
    if (checkDisabled || loading) return

    try {
      setLoading(true)
      setMessage('Проверка кода...')
      await delayAction(700)

      const result = await api.verifyEmailCode({
        email: data.email,
        code: data.code,
      })

      if (result.registerAction) {
        setIsLogin(false)
        setRegisterMessage(
          'Мы не нашли ваш аккаунт в нашей системе. После нажатия кнопки будет выполнена регистрация. Пожалуйста, подтвердите согласие с документами ниже',
        )
        setMessage('')
        return
      }

      if (result.accessToken) {
        const payload: AccessPayload = jwtDecode(result.accessToken)



        setMessage(isLogin ? 'Вы авторизованы' : 'Вы зарегистрированы')

        localStorage.setItem('accessToken', result.accessToken)
        if (result.refreshToken) {
          localStorage.setItem('refreshToken', result.refreshToken)
        }

        await delayAction(2000)

        const afterActionRoute = localStorage.getItem('afterActionRoute')
        const afterAction = localStorage.getItem('afterAction')

        setModalActive(false)

        if (afterActionRoute) {
          localStorage.removeItem('afterActionRoute')
          navigate(afterActionRoute)
          return
        }

        if (afterAction) {
          const parsedAction = JSON.parse(afterAction)

          if (parsedAction.action === AfterActions.PAYMENT) {
            dispatch(openPayment({ clusterId: parsedAction.clusterId, mode: 'cluster' }))
            localStorage.removeItem('afterAction')
            return
          }

          if (parsedAction.action === AfterActions.START_CHAT) {
            const topicsList = await getTopicList(payload.userId)
            const field = getFieldByGroup(parsedAction.group)
            const weHaveIt = topicsList?.find(
              (el) =>
                (el.userId === payload.userId || el.participantId === payload.userId) &&
                el[field] === parsedAction.order.id,
            )

            if (payload.contacts < minContacts && !weHaveIt) {
              dispatch(setCommonModalState({ modal: CommonModals.PAYMENT, state: true }))
              return
            }

            await openChatWindow(parsedAction.order, parsedAction.group, payload.userId)
            localStorage.removeItem('afterAction')
            return
          }
        }

        navigate(USER_ROUTE)
      }
    } catch (error: any) {
      const errorText = (error?.response?.data?.message || error?.message || '').toLowerCase()

      if (errorText.includes('timeout') && errorText.includes('exceeded')) {
        setMessage('Время ожидания истекло, попробуйте позже')
      } else if (error.response?.status === 401) {
        setMessage('Неверный или просроченный код')
      } else if (error.response?.status === 429) {
        setMessage('Слишком много попыток, повторите позже')
      } else {
        setMessage(error.message || 'Ошибка проверки кода')
      }
    } finally {
      setLoading(false)
    }
  }, [
    data.email,
    data.code,
    api,
    navigate,
    dispatch,
    setModalActive,
    isLogin,
    openChatWindow,
    getTopicList,
    checkDisabled,
    loading,
  ])

  const register = useCallback(async () => {
    if (!legalityChecked || loading) return

    try {
      setLoading(true)
      setMessage('Регистрация...')
      await delayAction(700)

      const result = await api.register({
        email: data.email,
        code: data.code,
      })

      if (result.accessToken) {
        const payload: AccessPayload = jwtDecode(result.accessToken)


        setMessage('Вы зарегистрированы')
        localStorage.setItem('accessToken', result.accessToken)
        if (result.refreshToken) {
          localStorage.setItem('refreshToken', result.refreshToken)
        }

        await delayAction(2000)

        const afterActionRoute = localStorage.getItem('afterActionRoute')
        const afterAction = localStorage.getItem('afterAction')

        setModalActive(false)

        if (afterActionRoute) {
          localStorage.removeItem('afterActionRoute')
          navigate(afterActionRoute)
          return
        }

        if (afterAction) {
          const parsedAction = JSON.parse(afterAction)

          if (parsedAction.action === AfterActions.PAYMENT) {
            dispatch(openPayment({ clusterId: parsedAction.clusterId, mode: 'cluster' }))
            localStorage.removeItem('afterAction')
            return
          }

          if (parsedAction.action === AfterActions.START_CHAT) {
            const topicsList = await getTopicList(payload.userId)
            const field = getFieldByGroup(parsedAction.group)
            const weHaveIt = topicsList?.find(
              (el) =>
                (el.userId === payload.userId || el.participantId === payload.userId) &&
                el[field] === parsedAction.order.id,
            )

            if (payload.contacts < minContacts && !weHaveIt) {
              dispatch(setCommonModalState({ modal: CommonModals.PAYMENT, state: true }))
              return
            }

            await openChatWindow(parsedAction.order, parsedAction.group, payload.userId)
            localStorage.removeItem('afterAction')
            return
          }
        }

        navigate(USER_ROUTE)
      }
    } catch (error: any) {
      const errorText = (error?.response?.data?.message || error?.message || '').toLowerCase()

      if (errorText.includes('timeout') && errorText.includes('exceeded')) {
        setMessage('Время ожидания истекло, попробуйте позже')
      } else if (error.response?.status === 401) {
        setMessage('Неверный или просроченный код')
      } else {
        setMessage(error.message || 'Ошибка регистрации')
      }
    } finally {
      setLoading(false)
    }
  }, [
    data.email,
    data.code,
    api,
    navigate,
    dispatch,
    setModalActive,
    legalityChecked,
    openChatWindow,
    getTopicList,
    loading,
  ])

  const resendCode = useCallback(() => {
    if (timer === 0 && !loading) {
      requestCode()
    }
  }, [timer, requestCode, loading])

  const toggleLegality = useCallback(() => {
    setLegalityChecked((prev) => !prev)
  }, [])

  const resetForm = useCallback(() => {
    setData(initialValue)
    setStage(EmailStage.EMAIL_INPUT)
    setMessage('')
    setRegisterMessage('')
    setTimer(0)
    setIsLogin(true)
    setLegalityChecked(false)
  }, [])

  return {
    inputHandler,
    data,
    getValidationResult,
    setDirty,
    stage,
    loading,
    message,
    registerMessage,
    isLogin,
    legalityChecked,
    timer,
    requestCode,
    verifyCode,
    register,
    resendCode,
    toggleLegality,
    disabled,
    checkDisabled,
    resetForm,
    setStage,
    setData,
  }
}
